if (!customElements.get("td-combined-listing")) {
  let pendingRequests = new Map();
  let prefetchRequests = new Set();
  let prefetchCache = new Map();
  let ongoingFetches = new Set();
  
  class TdCombinedListing extends HTMLElement {
    constructor() {
      super();
      this.products = this.querySelectorAll('[data-product-handle]');
      this.isDataSavingMode = navigator?.connection?.saveData
      this.fetchSectionsTimeout = null;
      this.prefetchSectionsTimeout = null;
      this.sections = document.querySelectorAll('[data-combined-listing]');
    }

    connectedCallback() {
      
      if (this.products) {
        this.products.forEach(prod => {
          prod.addEventListener('mouseover', () => {
            const productHandle = prod.dataset.productHandle;
            const variantId = prod.dataset.variantId;
            this.sections.forEach((section) => {
              const section_id = section.dataset.sectionId;
              this.prefetchSection(section_id, `/products/${productHandle}`, variantId);
            })
          });
          prod.addEventListener('click', () => {
            const productHandle = prod.dataset.productHandle;
            const variantId = prod.dataset.variantId;
            this.updateUrl(productHandle,variantId);
            this.sections.forEach((section) => {
              const section_id = section.dataset.sectionId;
              this.loadSection(section_id, `/products/${productHandle}`, variantId).then(
                (content) => {
                  document.querySelector(`[data-section-id='${section_id}']`).innerHTML = this.getSectionInnerHTML(content, `[data-section-id='${section_id}']`);
                  document.querySelector(`[data-section-id='${section_id}']`).dispatchEvent(new CustomEvent("section-content::changed", { bubbles: true, detail: section_id }));
                }
              );
            });
          });
        })

        window.addEventListener('popstate', function() {
          if (history.state && history.state.url === location.href) {
            location.reload();
          }
        });
      }
    }
    
    buildCacheKey(sectionId, path, variantId) {
      return `${path}:${sectionId}${variantId ? `:variant:${variantId}` : ''}`;
    }
    
    buildRequestGroups(sectionKeys) {
      const groups = sectionKeys.reduce(
        (acc, key) => {
          const [path, sectionId, , variantId] = key.split(':')
          const groupKey = `${path || ''}:${variantId || ''}`
    
          if (!acc[groupKey]) {
            acc[groupKey] = {
              path,
              variantId,
              keys: [],
              sectionIds: [],
            }
          }
          acc[groupKey].keys.push(key)
          acc[groupKey].sectionIds.push(sectionId)
          return acc
        },
        {}
      )
    
      return Object.values(groups)
    }
    
    scheduleFetchSections() {
      if (this.fetchSectionsTimeout) {
        clearTimeout(this.fetchSectionsTimeout)
      }
      this.fetchSectionsTimeout = setTimeout(() => this.fetchSections(), 100)
    }
    
    schedulePrefetchSections() {
      if (this.prefetchSectionsTimeout) {
        clearTimeout(this.prefetchSectionsTimeout)
      }
      this.prefetchSectionsTimeout = setTimeout(() => this.prefetchSections(), 200)
    }
    
    async fetchSections() {
      if (pendingRequests.size === 0) {
        return
      }
    
      const sectionKeys = Array.from(pendingRequests.keys())
      const requestGroups = this.buildRequestGroups(sectionKeys)
    
      try {
        await Promise.all(
          requestGroups.map(async ({ path, variantId, keys, sectionIds }) => {
            const url = new URL(window.location.href)
            if (path) {
              url.pathname = path
            }
            url.searchParams.set('sections', sectionIds.join(','))
            if (variantId) {
              url.searchParams.set('variant', variantId)
            }
            const requestUrl = url.toString()
    
            const response = await fetch(requestUrl)
            if (!response.ok) {
              throw new Error(`Failed to fetch: ${requestUrl}`)
            }
            const sectionsData = await response.json()
    
            keys.forEach((key, index) => {
              const callbacks = pendingRequests.get(key) || []
              pendingRequests.delete(key)
              const sectionId = sectionIds[index]
              const data = sectionsData[sectionId]
              if (data === undefined) {
                callbacks.forEach(({ reject }) => reject(new Error(`No data for section ${sectionId}`)))
              } else {
                callbacks.forEach(({ resolve }) => resolve(data))
                prefetchCache.set(key, data)
              }
              ongoingFetches.delete(key)
            })
          }),
        )
      } catch (error) {
        console.error(error)
        const sectionKeys = Array.from(pendingRequests.keys())
        sectionKeys.forEach((key) => {
          const callbacks = pendingRequests.get(key) || []
          pendingRequests.delete(key)
          callbacks.forEach(({ reject }) => reject(error))
          ongoingFetches.delete(key)
        })
      }
    }
    
    async prefetchSections() {
      if (prefetchRequests.size === 0) {
        return
      }
    
      const sectionKeys = Array.from(prefetchRequests)
      const requestGroups = this.buildRequestGroups(sectionKeys)
    
      try {
        await Promise.all(
          requestGroups.map(async ({ path, variantId, keys, sectionIds }) => {
            const url = new URL(window.location.href)
            if (path) {
              url.pathname = path
            }
            url.searchParams.set('sections', sectionIds.join(','))
            if (variantId) {
              url.searchParams.set('variant', variantId)
            }
            const requestUrl = url.toString()
    
            const response = await fetch(requestUrl)
            if (!response.ok) {
              throw new Error(`Failed to prefetch: ${requestUrl}`)
            }
            const sectionsData = await response.json()
    
            keys.forEach((key, index) => {
              const sectionId = sectionIds[index]
              const data = sectionsData[sectionId]
              if (data !== undefined) {
                prefetchCache.set(key, data)
              }
              prefetchRequests.delete(key)
              ongoingFetches.delete(key)
    
              const callbacks = pendingRequests.get(key) || []
              pendingRequests.delete(key)
              callbacks.forEach(({ resolve, reject }) => {
                if (data !== undefined) {
                  resolve(data)
                } else {
                  reject(new Error(`No data for section ${sectionId}`))
                }
              })
            })
          }),
        )
      } catch (error) {
        console.error(error)
        const sectionKeys = Array.from(prefetchRequests)
        sectionKeys.forEach((key) => {
          prefetchRequests.delete(key)
          ongoingFetches.delete(key)
          const callbacks = pendingRequests.get(key) || []
          pendingRequests.delete(key)
          callbacks.forEach(({ reject }) => reject(error))
        })
      }
    }
    
    loadSection(sectionId, path = '', variantId) {
      const key = this.buildCacheKey(sectionId, path, variantId)
    
      if (prefetchCache.has(key)) {
        return Promise.resolve(prefetchCache.get(key))
      }
    
      return new Promise((resolve, reject) => {
        if (!pendingRequests.has(key)) {
          pendingRequests.set(key, [])
        }
        pendingRequests.get(key)?.push({ resolve, reject })
    
        if (!ongoingFetches.has(key) && !prefetchRequests.has(key)) {
          ongoingFetches.add(key)
          this.scheduleFetchSections()
        }
      })
    }
    
    prefetchSection(sectionId, path = '', variantId) {
      if (this.isDataSavingMode) {
        return
      }
    
      const key = this.buildCacheKey(sectionId, path, variantId)
    
      if (pendingRequests.has(key) || ongoingFetches.has(key) || prefetchCache.has(key)) {
        return
      }
    
      if (!prefetchRequests.has(key)) {
        prefetchRequests.add(key)
        this.schedulePrefetchSections()
      }
    }
    
    cancelPrefetchSection(sectionId, path = '', variantId) {
      const key = this.buildCacheKey(sectionId, path, variantId)
      prefetchRequests.delete(key)
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    updateUrl(productHandle, variantId) {
      let newUrl = `/products/${productHandle}`;
      const currentUrl = new URL(window.location.href);
      const currentParams = new URLSearchParams(currentUrl.search);
    
      currentParams.forEach((value, key) => {
        if (key === 'variant' || key === 'selling_plan') {
          currentParams.delete(key);
        }
      });

      if (variantId) {
        currentParams.set('variant', variantId);
      }
    
      const finalUrl = `${currentUrl.origin}${newUrl}?${currentParams.toString()}`;
    
      history.pushState({ url: finalUrl }, null, finalUrl);
    }
  }

  customElements.define("td-combined-listing", TdCombinedListing);
}

(function() {
  // Standalone function for main color management based on product images
  function initProductColorManager() {
    let defaultProductColor = null;
    const productContainer = document.querySelector('.td-product-main');
    
    if (productContainer && productContainer.hasAttribute('data-images-primary-color')) {
      defaultProductColor = getComputedStyle(productContainer).getPropertyValue('--product-color');
      document.addEventListener('slide-changed', (event) => {
        const { activeSlide, sliderId } = event.detail;
        updateProductColorFromSlide(activeSlide, sliderId);
      });
    }
    
    function updateProductColorFromSlide(activeSlide, sliderId) {
      if (!sliderId || !activeSlide) return;

      let slider = productContainer.querySelector(`#${sliderId}`);
      if (!slider) return;

      const mediaElement = activeSlide.querySelector('.td-product__media');
      if (!mediaElement) return;
      const mediaColor = mediaElement.dataset.mediaColor;
      updateProductColor(mediaColor);
    }
    
    function updateProductColor(mediaColor) {
      if (mediaColor) {
        productContainer.style.setProperty('--product-color', mediaColor);
      } else if (defaultProductColor) {
        productContainer.style.setProperty('--product-color', defaultProductColor);
      }
    }
  }
  initProductColorManager();
})();
